// app/src/lib/cashflow/util.js — date + integer-money helpers for the cashflow library.

import { isOn as isDemo, obscure } from '../demo.js';

// LOCAL calendar date (YYYY-MM-DD), in the device's timezone — NOT UTC. Using
// toISOString() here rolled "today"/"this month" to the next day in the evening
// for users west of UTC (e.g. 5pm PT on May 31 → UTC June 1 → empty "June").
export function isoToday() { return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10); }

export function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(a, b) {
  return Math.round((toUTC(a) - toUTC(b)) / 86_400_000);
}

function toUTC(iso) { return new Date(iso + 'T00:00:00Z').getTime(); }

/**
 * Return the ISO Monday for the week containing `iso` (week starts on Monday).
 * The forecast uses Monday-start weeks because most payroll cycles align to
 * Friday-or-end-of-week.
 */
export function mondayOf(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

export function cents(n) {
  if (n == null || n === '') return 0;
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 100);
}

/** Convert a Plaid signed amount (positive = outflow) into local convention. */
export function plaidAmountToOutflowCents(amount) {
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount * 100);
}

// Card feeds give a clean merchant_name, but bank / ACH rows arrive as a raw
// NACHA descriptor like "ORIG CO NAME:LAM SEAFOOD ORIG ID:… CO ENTRY DESCR:…"
// (ADP payroll runs are the most common). Displays show only the merchant, so
// pull the value after NAME: (up to the next KEY: token or the end) and drop
// the rest — the bank statement has the full string for anyone who needs it.
// Descriptors without a NAME: field (most card charges) pass through unchanged.
// The next field is introduced by a known NACHA key label — cut the name there.
// (A generic "uppercase words then colon" boundary wrongly eats multi-word
// merchant names like "LAM SEAFOOD ORIG ID:…" → "LAM".)
const NACHA_KEY = /\s+(?:ORIG ID|CO ID|CO ENTRY DESCR|ENTRY DESCR|DESC DATE|DESC|SEC|IND ID|IND NAME|PPD ID|WEB ID|CCD ID|TEL ID|TRACE(?:\s*NO)?|REF|DES|EED|BATCH|ID)\s*:/i;

/**
 * The NACHA NAME: extraction WITHOUT demo obscuring — safe for matching keys
 * (e.g. spendCategory.ruleKey falls back to this for bank rows whose Plaid
 * merchant_name is blank). Never use for display; that's cleanTxnName.
 */
export function rawTxnName(label) {
  const s = String(label || '').trim();
  if (!s) return '';
  const idx = s.search(/NAME:/i);
  if (idx < 0) return s;                       // no NACHA NAME: field — pass through
  const after = s.slice(idx + 5);
  const cut = after.search(NACHA_KEY);
  return (cut >= 0 ? after.slice(0, cut) : after).replace(/\s{2,}/g, ' ').trim() || s;
}

export function cleanTxnName(label) {
  const cleaned = rawTxnName(label);
  if (!cleaned) return '';
  // Demo mode: swap the (cleaned) merchant for a fictional one. This is the
  // single chokepoint for merchant names on Cashflow, Today and Reports — all
  // three route their displays through here — so obscuring at this one point
  // keeps the same merchant reading identically across every screen. cleanTxnName
  // is display-only (never feeds matching/dedup), so this is safe.
  return isDemo() ? obscure(cleaned, 'merchant') : cleaned;
}
